import { IBillingPlan, IBillingPlanV2, IPrice } from "../../../interfaces/models/billingPlan"
import { useAppActions } from "../../../stores/application/useAppActions"
import { useAuthActions } from "../../../stores/auth/useAuthActions"
import { useAuthStore } from "../../../stores/auth/useAuthStore"
import { useBillingActions } from "../../../stores/billing/useBillingActions"
import { useBillingStore } from "../../../stores/billing/useBillingStore"
import { IBuySubscriptionParams, IResumeSubscriptionParams, IUpcomingPaymentParams, useBillingRequest } from "../../apis/billing/useBillingRequest"
import { useAuthHandlers } from "../auth/useAuthHandlers"

export const useBillingHandlers = () => {

    //hooks
    const { setToast } = useAppActions()
    const { setBillingPlans,
        setSelectedBillingPlan,
        setSelectedPrice,
        setAppliedCoupon,
        setUpcomingPayment,
        setBillingPlansFeatures } = useBillingActions()
    const { store: { billingPlans } } = useBillingStore()
    const { getBillingPlansRequestHandler,
        getSetupIntentRequestHandler,
        applyCouponRequestHandler,
        buySubscriptionRequestHandler,
        getInvoicesRequestHandler,
        addPaymentMethodRequestHandler,
        setDefaultPaymentMethodRequestHandler,
        calculateUpcomingPaymentRequestHandler,
        getCurrentPayingPriceRequestHandler,
        resumeSubscriptionRequestHandler,
        getBillingPlansFeaturesRequestHandler,
        updatePaymentIntentWithPaymentMethod,
        payInvoiceRequest,
        voidInvoiceRequest } = useBillingRequest()
    const { store: { subscription } } = useAuthStore()
    const { setLifetime, setSubscription } = useAuthActions()
    const { getUserHandler } = useAuthHandlers()

    //handlers
    const setSelectedBillingPlanHandler = (payload: string) => {
        //Calculate upcoming payment - apply coupon isto
        setSelectedBillingPlan(billingPlans.find((billingPlan: IBillingPlan) => billingPlan.interval === payload))
    }

    const setSelectedPlanAndPriceHandler = (plan: IBillingPlanV2, interval: string, currency: string) => {
        setSelectedBillingPlan(plan)
        let price: IPrice | undefined;
        const matchingPrices = plan.prices.filter((price: IPrice) => price.interval === interval && price.currency === currency);
        
        if (matchingPrices.length === 1) {
            // If only one price for this interval+currency, use it (backward compatibility)
            price = matchingPrices[0];
        } else if (matchingPrices.length > 1) {
            // If multiple prices, prefer active ones
            price = matchingPrices.find((price: IPrice) => price.active === true) ?? matchingPrices[0];
        }
        
        setSelectedPrice(price ?? undefined)
        return price
    }

    const getBillingPlansHandler = async () => {
        let plans = await getBillingPlansSettingsHandler()
        if (plans && Array.isArray(plans) && plans.length) {
        

            //read through these and select the best plan based on the current subscription
            /* let selectedPlan;
            const promoCouponCode = !!localStorage.getItem('promoCouponCode')

            if (validSubscription) {
                subscription?.status !== 'active' ?
                    selectedPlan = plans.find((billingPlan: IBillingPlan) => billingPlan.interval === 'year' && version === billingPlan.version) :
                    selectedPlan = plans.find((billingPlan: IBillingPlan) => billingPlan.priceId === subscription.priceId)
                version = selectedPlan ? selectedPlan.version : version
            }

            plans = result.plans.filter((billingPlan: IBillingPlan) => billingPlan.currency === (subscription?.currency ?? 'usd') && version === billingPlan.version) */

            /* if (validSubscription && context !== "workspace") {
                selectedPlan =
                    promoCouponCode ?
                        plans.find((billingPlan: IBillingPlan) => billingPlan.interval === 'year' && version === billingPlan.version) :
                        plans.find((billingPlan: IBillingPlan) => billingPlan.interval !== subscription.interval && version === billingPlan.version)
            } */

            /* if (promoCouponCode && !validSubscription && context !== 'workspace') {
                selectedPlan = plans.find((billingPlan: IBillingPlan) => billingPlan.interval === 'year' && version === billingPlan.version)
            } */
            
            // filter plans to exclude any (old plans) other than version 8
            plans = plans.filter((plan: any) => plan.version === 8);

            setBillingPlans(plans)
            //let selectedPlan = plans.find((plan: any) => plan._id === "666b05b255c9f602da7db111")
            plans.forEach((plan: any) => {
                if(plan.prices.some((price: any) => subscription.priceId === price.priceId))
                setSelectedPlanAndPriceHandler(plan, subscription.interval, subscription.currency)
            });
        }
    }

    const getBillingPlansSettingsHandler = async () => {
        const result = await getBillingPlansRequestHandler()
        if (result && Array.isArray(result.plans)) {
            let { plans } = result;
            plans = plans.filter((plan: any) => plan.version === 7 || plan.version === 8)
                    .map((plan: any) => ({
                ...plan,
                name: plan.name.replace(/ 2\.0$/, "")
            }));
            return plans
        }
        return []
    }

    const getSetupIntentHandler = async () => {
        const result = await getSetupIntentRequestHandler()
        //result && setSetupIntent(result)
        return result
    }

    const buySubscriptionHandler = async (buySubscriptionParams: IBuySubscriptionParams) => {
        const result = await buySubscriptionRequestHandler(buySubscriptionParams)
        if(result) {
            setSubscription(result)
            getUserHandler()
            // sets new status in local storage in order to other tabs get updated after revisiting
            window.localStorage.setItem("newSubscriptionStatus", result?.status);
        }
        return result
    }

    const applyCouponHandler = async (couponCode: string) => {
        const result = await applyCouponRequestHandler(couponCode)
        result?.coupon && result?.type === 'stripe' && setAppliedCoupon({ ...result.coupon, couponCode })
        result?.type === 'native' && setLifetime(true) //todo: check to see what happens
    }
    const removeCouponHandler = async () => {
        setAppliedCoupon(undefined)
        setToast({ type: 'success', message: "Action succeeded!" })
    }
    const getInvoicesHandler = async (invoiceId?: string) => {
        const result = await getInvoicesRequestHandler(invoiceId)
        return result ?? []
    }

    const addPaymentMethodHandler = async (paymentMethodId: string, attachToCustomer?: boolean, refetchUser?: boolean) => {
        await addPaymentMethodRequestHandler(paymentMethodId, attachToCustomer);
        refetchUser && getUserHandler()
    }

    const updatePaymentIntentWithPaymentMethodHandler = async (paymentIntentId: string, paymentMethodId: string,  calculatedPrice: any, oldClientSecret?: string, credits?: number) => {
        const result = await updatePaymentIntentWithPaymentMethod(paymentIntentId, paymentMethodId, calculatedPrice, oldClientSecret, credits);
        return result;
    }

    const calculateUpcomingPaymentHandler = async (params: IUpcomingPaymentParams) => {
        const result = await calculateUpcomingPaymentRequestHandler(params)
        setUpcomingPayment(result)
        return result
    }

    const getCurrentPayingPriceHandler = async (priceId?: string) => {
        const result = await getCurrentPayingPriceRequestHandler(priceId)
        return result
    }

    const onResumeSubscriptionHandler = async (resume?: IResumeSubscriptionParams) => {
        const data = await resumeSubscriptionRequestHandler(resume)
        data && !data.pausedUntil && getUserHandler()
    }

    const getPlansFeaturesHandler = async () => {
        const result = await getBillingPlansFeaturesRequestHandler();
        if (result && Array.isArray(result.features)) {
            const features = result.features;
            const creditsIndex = features.findIndex((f: { category?: string }) => f && f.category === "Credits");
            if (creditsIndex > 0) {
                const [creditsCategory] = features.splice(creditsIndex, 1);
                features.unshift(creditsCategory);
            }
            setBillingPlansFeatures(features);
        } else {
            setBillingPlansFeatures([]);
        }

        return result;
    }

    const payInvoiceRequestHandler = async (invoiceId: string, paymentMethodId: string) => {
        const result = await payInvoiceRequest(invoiceId, paymentMethodId);
        return result;
    }

    const voidInvoiceRequestHandler = async (invoiceId: string) => {
        const result = await voidInvoiceRequest(invoiceId);
        return result;
    }

    const setDefaultPaymentMethodHandler = async (paymentMethodId: string) => {
        const result = await setDefaultPaymentMethodRequestHandler(paymentMethodId);
        if (result?.stripePaymentMethods) {
            console.log('result111: ', result);
            setSubscription({ ...subscription, paymentMethods: result.stripePaymentMethods });
        }
        return result;
    }

    return {
        setSelectedBillingPlanHandler,
        setSelectedPlanAndPriceHandler,
        getBillingPlansHandler,
        getBillingPlansSettingsHandler,
        getSetupIntentHandler,
        buySubscriptionHandler,
        applyCouponHandler,
        removeCouponHandler,
        getInvoicesHandler,
        addPaymentMethodHandler,
        calculateUpcomingPaymentHandler,
        getCurrentPayingPriceHandler,
        onResumeSubscriptionHandler,
        getPlansFeaturesHandler,
        updatePaymentIntentWithPaymentMethodHandler,
        payInvoiceRequestHandler,
        voidInvoiceRequestHandler,
        setDefaultPaymentMethodHandler
    }
}